// ============================================
// Data Types & Dynamic Rendering Helpers
// For progressive disclosure - Maya presents, we render
// ============================================

// --- Types for Maya's tool parameters ---

export interface MayaResort {
  name: string;
  tagline: string;
  highlight?: boolean;
}

export interface MayaFlightOption {
  departure_time: string;
  arrival_time: string;
  price_from: string;
  cabin_class?: string;
}

export interface ShowResortsData {
  destination: string;
  resorts: MayaResort[];
}

export interface ShowFlightsData {
  route: string;
  origin?: string;
  origin_code?: string;
  destination?: string;
  destination_code?: string;
  outbound_date: string;
  return_date?: string;
  flights: MayaFlightOption[];
}

// --- Legacy types (for fallback) ---

export interface Flight {
  id: string;
  departure: string;
  arrival: string;
  departureTime: string;
  arrivalTime: string;
  duration: string;
  price: number;
  class: 'Economy' | 'Premium' | 'Upper Class';
  stops: number;
}

export interface Resort {
  id: string;
  name: string;
  location: string;
  rating: number;
  pricePerNight: number;
  image: string;
  highlights: string[];
}

// --- Stock Resort Images ---
// Premium resort imagery for any resort Maya mentions

const RESORT_IMAGES = [
  'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=600&q=80', // Luxury pool
  'https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?w=600&q=80',   // Ocean resort
  'https://images.unsplash.com/photo-1520250497591-112f2f40a3f4?w=600&q=80', // Beach chairs
  'https://images.unsplash.com/photo-1582719508461-905c673771fd?w=600&q=80', // Tropical pool
  'https://images.unsplash.com/photo-1571896349842-33c89424de2d?w=600&q=80', // Beach resort
  'https://images.unsplash.com/photo-1540541338287-41700207dee6?w=600&q=80', // Caribbean beach
];

// Known resort images (for specific resorts Maya might mention)
const KNOWN_RESORT_IMAGES: Record<string, string> = {
  'sandy lane': 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=600&q=80',
  'cobblers cove': 'https://images.unsplash.com/photo-1520250497591-112f2f40a3f4?w=600&q=80',
  'the crane': 'https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?w=600&q=80',
  'the sandpiper': 'https://images.unsplash.com/photo-1571896349842-33c89424de2d?w=600&q=80',
  'sea breeze': 'https://images.unsplash.com/photo-1582719508461-905c673771fd?w=600&q=80',
  'coral reef club': 'https://images.unsplash.com/photo-1540541338287-41700207dee6?w=600&q=80',
  'lone star': 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=600&q=80',
  'the house': 'https://images.unsplash.com/photo-1520250497591-112f2f40a3f4?w=600&q=80',
  'treasure beach': 'https://images.unsplash.com/photo-1571896349842-33c89424de2d?w=600&q=80',
  'round hill': 'https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?w=600&q=80',
  'half moon': 'https://images.unsplash.com/photo-1582719508461-905c673771fd?w=600&q=80',
  'sugar beach': 'https://images.unsplash.com/photo-1540541338287-41700207dee6?w=600&q=80',
  'jade mountain': 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=600&q=80',
  'curtain bluff': 'https://images.unsplash.com/photo-1520250497591-112f2f40a3f4?w=600&q=80',
  'jumby bay': 'https://images.unsplash.com/photo-1571896349842-33c89424de2d?w=600&q=80',
};

// Resort highlights - "Why Maya loves it"
const RESORT_HIGHLIGHTS: Record<string, string[]> = {
  'sandy lane': [
    'Private powdery white beach',
    'World-class spa sanctuary',
    'Three championship golf courses',
    'Where celebrities disappear'
  ],
  'cobblers cove': [
    'Intimate boutique atmosphere',
    'Garden suites with ocean views',
    'Exceptional personalized service',
    'Award-winning restaurant'
  ],
  'the crane': [
    'Historic cliff-top setting',
    'Infinity pools overlooking the Atlantic',
    'Pink sand beach below',
    'One of the Caribbean\'s oldest hotels'
  ],
  'the sandpiper': [
    'Sister property to Coral Reef Club',
    'Perfect for families',
    'Two stunning beaches',
    'Relaxed, unpretentious vibe'
  ],
  'coral reef club': [
    'Twelve acres of tropical gardens',
    'Two private beaches',
    'Outstanding cuisine',
    'Genuine Bajan hospitality'
  ],
  'the house': [
    'Adults-only serenity',
    'Beachfront suites',
    'Personal ambassador service',
    'Contemporary Caribbean style'
  ],
  'round hill': [
    'Ralph Lauren-designed luxury',
    'Private villa experience',
    'Legendary celebrity history',
    'Stunning Montego Bay views'
  ],
  'half moon': [
    '400 acres of tropical paradise',
    'Private beach coves',
    'Equestrian centre',
    'Championship golf course'
  ],
  'sugar beach': [
    'Between the Pitons',
    'UNESCO World Heritage setting',
    'Rainforest sanctuary',
    'Overwater bungalows'
  ],
  'jade mountain': [
    'Open-wall infinity pool sanctuaries',
    'Architectural masterpiece',
    'Piton views from every room',
    'No TVs or phones - pure escape'
  ],
  'curtain bluff': [
    'All-inclusive luxury',
    'Two pristine beaches',
    'Exceptional tennis facilities',
    'Old-school Caribbean elegance'
  ],
  'jumby bay': [
    'Private island exclusivity',
    'No cars, just bicycles',
    'Two miles of beaches',
    'Ultimate privacy'
  ],
};

// Default highlights for unknown resorts
const DEFAULT_HIGHLIGHTS = [
  'Stunning beachfront location',
  'Exceptional service',
  'Beautiful tropical gardens',
  'Perfect Caribbean escape'
];

// --- Cabin Class Experience Data ---
// The upsell moment - what each class FEELS like

export interface CabinExperience {
  name: string;
  tagline: string;
  image: string;
  highlights: { icon: string; text: string }[];
  priceHint?: string;
}

const CABIN_EXPERIENCES: Record<string, CabinExperience> = {
  'Economy': {
    name: 'Economy',
    tagline: 'Everything you need, nothing you don\'t',
    image: 'https://images.unsplash.com/photo-1540339832862-474599807836?w=800&q=80',
    highlights: [
      { icon: 'meal', text: 'Complimentary meals & drinks' },
      { icon: 'screen', text: 'Personal seatback entertainment' },
      { icon: 'power', text: 'USB & power at every seat' },
      { icon: 'legroom', text: 'Generous 31" seat pitch' }
    ]
  },
  'Premium': {
    name: 'Premium',
    tagline: 'A little extra goes a long way',
    image: 'https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=800&q=80',
    highlights: [
      { icon: 'checkin', text: 'Dedicated check-in & boarding' },
      { icon: 'seat', text: 'Extra-wide leather seats (38" pitch)' },
      { icon: 'dining', text: 'Enhanced dining with wine pairing' },
      { icon: 'bag', text: 'Priority baggage handling' },
      { icon: 'amenity', text: 'Amenity kit & extra comfort' }
    ]
  },
  'Upper Class': {
    name: 'Upper Class',
    tagline: 'Your journey begins before takeoff',
    image: 'https://flywith.virginatlantic.com/content/dam/experience/cabins/neo/april-2024/VA_SHOT_9_CREW_SERVING_DRINKS_COUPLE_02696.jpg.transform/1280x708/image.jpg',
    highlights: [
      { icon: 'chauffeur', text: 'Door-to-door chauffeur service' },
      { icon: 'lounge', text: 'The Clubhouse lounge & spa' },
      { icon: 'bed', text: 'Lie-flat suite with privacy' },
      { icon: 'bar', text: 'The Bar - social space at 40,000ft' },
      { icon: 'dining', text: 'Fine dining, whenever you want it' },
      { icon: 'arrivals', text: 'Arrivals lounge & spa access' }
    ],
    priceHint: 'The journey IS the destination'
  }
};

export function getCabinExperience(cabinClass: string): CabinExperience {
  const normalized = normalizeCabinClass(cabinClass);
  return CABIN_EXPERIENCES[normalized] || CABIN_EXPERIENCES['Economy'];
}

export function getResortHighlights(resortName: string): string[] {
  const normalized = resortName.toLowerCase();
  for (const [key, highlights] of Object.entries(RESORT_HIGHLIGHTS)) {
    if (normalized.includes(key)) {
      return highlights;
    }
  }
  return DEFAULT_HIGHLIGHTS;
}

export function getResortImage(resortName: string, index: number): string {
  const normalized = resortName.toLowerCase();

  // Check for known resort
  for (const [key, url] of Object.entries(KNOWN_RESORT_IMAGES)) {
    if (normalized.includes(key)) {
      return url;
    }
  }

  // Fall back to stock images (rotate through them)
  return RESORT_IMAGES[index % RESORT_IMAGES.length];
}

// --- Airport Codes ---

const AIRPORT_CODES: Record<string, string> = {
  'london': 'LHR',
  'london heathrow': 'LHR',
  'heathrow': 'LHR',
  'gatwick': 'LGW',
  'london gatwick': 'LGW',
  'manchester': 'MAN',
  'birmingham': 'BHX',
  'edinburgh': 'EDI',
  'glasgow': 'GLA',
  'barbados': 'BGI',
  'bridgetown': 'BGI',
  'antigua': 'ANU',
  'jamaica': 'MBJ',
  'montego bay': 'MBJ',
  'st lucia': 'UVF',
  'saint lucia': 'UVF',
  'grenada': 'GND',
  'tobago': 'TAB',
  'trinidad': 'POS',
  'bahamas': 'NAS',
  'nassau': 'NAS',
  'new york': 'JFK',
  'los angeles': 'LAX',
  'miami': 'MIA',
  'orlando': 'MCO',
  'las vegas': 'LAS',
};

export function getAirportCode(location: string): string {
  const normalized = location.toLowerCase().trim();
  return AIRPORT_CODES[normalized] || location.slice(0, 3).toUpperCase();
}

// --- Flight Number Generator ---

export function generateFlightNumber(index: number): string {
  // Virgin Atlantic flight numbers typically VS + 3 digits
  const baseNumbers = [29, 33, 45, 69, 75, 97, 103, 121];
  return `VS${String(baseNumbers[index % baseNumbers.length]).padStart(3, '0')}`;
}

// --- Duration Calculator ---

export function estimateFlightDuration(origin: string, destination: string): string {
  // Caribbean routes are typically 8-10 hours from UK
  const routes: Record<string, string> = {
    'BGI': '8h 30m',
    'ANU': '8h 45m',
    'MBJ': '9h 30m',
    'UVF': '8h 45m',
    'GND': '8h 30m',
    'NAS': '8h 45m',
    'JFK': '7h 30m',
    'LAX': '10h 45m',
    'MIA': '9h 15m',
  };

  const destCode = getAirportCode(destination);
  return routes[destCode] || '8h 30m';
}

// --- Price Formatter ---

export function parsePrice(priceStr: string): number {
  // Extract number from strings like "£649pp", "£1,249", "from £899"
  const match = priceStr.replace(/,/g, '').match(/(\d+)/);
  return match ? parseInt(match[1], 10) : 649;
}

// --- Cabin Class Normalization ---

export function normalizeCabinClass(cabin?: string): 'Economy' | 'Premium' | 'Upper Class' {
  if (!cabin) return 'Economy';
  const lower = cabin.toLowerCase();
  if (lower.includes('upper')) return 'Upper Class';
  if (lower.includes('premium')) return 'Premium';
  return 'Economy';
}

// --- Legacy Mock Data (fallback only) ---

export const MOCK_FLIGHTS: Flight[] = [
  {
    id: 'VS029',
    departure: 'London Heathrow',
    arrival: 'Barbados',
    departureTime: '09:15',
    arrivalTime: '13:45',
    duration: '8h 30m',
    price: 649,
    class: 'Economy',
    stops: 0
  },
  {
    id: 'VS033',
    departure: 'London Heathrow',
    arrival: 'Barbados',
    departureTime: '10:30',
    arrivalTime: '15:00',
    duration: '8h 30m',
    price: 1249,
    class: 'Premium',
    stops: 0
  },
  {
    id: 'VS045',
    departure: 'London Heathrow',
    arrival: 'Barbados',
    departureTime: '11:45',
    arrivalTime: '16:15',
    duration: '8h 30m',
    price: 2899,
    class: 'Upper Class',
    stops: 0
  },
];

export const MOCK_RESORTS: Resort[] = [
  {
    id: 'R001',
    name: 'Sandy Lane',
    location: 'St James, Barbados',
    rating: 5,
    pricePerNight: 1200,
    image: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=600&q=80',
    highlights: ['Private beach', 'Spa', 'Golf course', 'Fine dining']
  },
  {
    id: 'R002',
    name: 'Cobblers Cove',
    location: 'St Peter, Barbados',
    rating: 4.5,
    pricePerNight: 380,
    image: 'https://images.unsplash.com/photo-1520250497591-112f2f40a3f4?w=600&q=80',
    highlights: ['Boutique hotel', 'Garden suites', 'Water sports']
  },
  {
    id: 'R003',
    name: 'The Crane Resort',
    location: 'St Philip, Barbados',
    rating: 4.5,
    pricePerNight: 450,
    image: 'https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?w=600&q=80',
    highlights: ['Historic property', 'Cliff-top pools', 'Ocean views']
  },
];
